import { useEffect, useState } from 'react';
import {
  type Control,
  Controller,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Col, Form, Row, Space, theme, Typography } from 'antd';
import { Dayjs } from 'dayjs';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import {
  type LinkedTechniciansGroup,
  useLocationLinkedTechnicians,
} from 'pos-client/hooks/useLocationLinkedTechnicians.ts';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type AssignTechnicianRequestModel,
  type AssigTechnicianFormModel,
  AssigTechnicianFormSchema,
  type ResourceWithAssetModel,
  type ServiceTypesRequestModel,
  ServiceTypesRequestSchema,
} from 'pos-shared/models/schedulingSchema.ts';

import { TechnicianSelect } from './TechnicianSelect.tsx';
import { useConfirmNonScheduledTechnician } from './useConfirmNonScheduledTechnician.tsx';

export type CustomModalPropsData = {
  resource: Partial<ResourceWithAssetModel>;
  startTime: Dayjs;
  endTime: Dayjs;
  serviceLocationId: string;
};
export interface CustomModalProps<T> {
  isVisible: boolean;
  data: T;
  handleOk: () => void;
  handleCancel: () => void;
  serviceTypeFilter: ServiceTypesRequestModel;
}

export const AssignTechnicianModal = ({
  isVisible,
  data,
  handleOk,
  handleCancel,
  serviceTypeFilter,
}: CustomModalProps<CustomModalPropsData>) => {
  const { serviceLocationId } = data;
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const trpcContext = trpc.useUtils();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    values: {
      technician: '',
    },
    resolver: zodResolver(AssigTechnicianFormSchema),
  });
  const { confirmNonScheduledTechnician } = useConfirmNonScheduledTechnician();

  const selectedTechnician = watch('technician');
  const [technicianNotScheduled, setTechnicianNotScheduled] = useState(false);
  const serviceLocationOpenTime = data.startTime.format('YYYY-MM-DDTHH:mm:ss');
  const serviceLocationCloseTime = data.endTime.format('YYYY-MM-DDTHH:mm:ss');

  const { showError, showSuccess } = useNotifications();

  const { Title } = Typography;
  const { technicians, isLoadingTechnicians } = useLocationLinkedTechnicians({
    serviceLocationId,
    startTime: serviceLocationOpenTime,
    endTime: serviceLocationCloseTime,
    assigningToAJob: false,
  });

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: () => handleCancel(),
  });

  const onCancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      handleCancel();
    }
  };

  const assignTechnicianMutationSuccess = async () => {
    handleOk();
    showSuccess(
      t('common.notification.success.message', {
        action: 'assigned',
        entity: 'technician',
      })
    );

    await trpcContext.getConflictLanes.invalidate();
  };

  const confirmHandler = (assignmentData: AssignTechnicianRequestModel) => {
    if (technicianNotScheduled) {
      const techniciansBeingAssigned =
        technicians.nonScheduledTechnicians.filter(
          (technician) => technician.id === selectedTechnician
        );

      confirmNonScheduledTechnician(
        () => assignTechnician(assignmentData),
        techniciansBeingAssigned
      );
    } else {
      assignTechnician(assignmentData);
    }
  };

  const { mutate: assignTechnician, isLoading: isAssigning } =
    trpc.assignTechnician.useMutation({
      onSuccess: assignTechnicianMutationSuccess,
      onError: async () => {
        showError(
          t('common.errors.error'),
          t('technicians.assignTechnicianError')
        );
      },
    });

  const handleAssignTechnician: SubmitHandler<AssigTechnicianFormModel> = (
    asset
  ) => {
    const assignmentData: AssignTechnicianRequestModel = {
      assetId: data?.resource?.id || '',
      serviceLocationId: data?.serviceLocationId,
      technicianId: asset.technician,
      dateOfService: data?.startTime.format('YYYY-MM-DD'),
    };

    confirmHandler(assignmentData);
  };

  useEffect(() => {
    if (
      technicians.nonScheduledTechnicians.find(
        (technician) => technician.id === selectedTechnician
      )
    ) {
      setTechnicianNotScheduled(true);
    } else if (technicianNotScheduled) {
      setTechnicianNotScheduled(false);
    }
  }, [selectedTechnician]);

  return (
    <PosModal
      header={{
        title:
          serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile
            ? t('routes.routeSettings')
            : data?.resource.name,
      }}
      open={isVisible}
      afterClose={reset}
      onCancel={onCancelModal}
      width={token.screenLG}
      footer={
        <Space>
          <Button onClick={onCancelModal}>{t('common.buttons.cancel')}</Button>
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit(handleAssignTechnician)}
            disabled={!isDirty}
            loading={isAssigning}
          >
            {t('technicians.assignTechnician')}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Title level={4}>
          {serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile
            ? t('routes.manageRouteSettings')
            : t('technicians.assignTechnician')}
        </Title>
        <TechnicianAssignmentContent
          control={control}
          technicians={technicians}
          loading={isLoadingTechnicians}
        />

        {technicianNotScheduled && (
          <Row
            style={{
              padding: token.marginLG,
              textAlign: 'center',
            }}
            align="top"
            justify="center"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              color={token.colorWarning}
              style={{
                marginRight: token.marginXXS,
                marginTop: '4px',
              }}
            />
            <Typography.Text
              style={{
                color: token.colorWarning,
              }}
            >
              {t('technicians.selectNonScheduledTechnicianExtraWarning')}
            </Typography.Text>
          </Row>
        )}
      </Form>
    </PosModal>
  );
};

const TechnicianAssignmentContent = ({
  control,
  technicians,
  loading,
}: {
  control: Control<AssigTechnicianFormModel>;
  technicians: LinkedTechniciansGroup;
  loading: boolean;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <>
      <Row>
        <Col
          style={{
            marginBottom: token.marginLG,
            color: token.colorTextDescription,
          }}
          span={24}
        >
          {t('scheduling.lanes.selectTechnicianLaneDescription')}
        </Col>
      </Row>
      <Row>
        <Col span={11}>
          <Controller
            control={control}
            name="technician"
            render={({ field: { onChange, value } }) => (
              <Form.Item label={t('technicians.technician')}>
                <TechnicianSelect
                  technicians={technicians}
                  loading={loading}
                  onChange={onChange}
                  value={value}
                />
              </Form.Item>
            )}
          />
        </Col>
      </Row>
    </>
  );
};
